import React, { useState,useEffect,useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from '../../plugins/axios'
import moment from "moment";
import { mmHgToKPa, celsiusToFahrenheit, percentTommHg,percentToKpa,mmHgToPercent,valueStringForPlayer, checkEmptyValue } from '../../plugins/common'
import { useRecoilState } from 'recoil';
import { unitState } from '../../plugins/recoil';
import { useTranslation } from 'react-i18next'

import Wave from './alarmform/wave';

import "./A4.scss";

const AlarmForm = () => {
    const { t, i18n } = useTranslation()
    const [searchParams] = useSearchParams();
    const isFirstRender = useRef(true);
    const [queryParams, setQueryParams] = useState({})
    const [units, setUnits] = useRecoilState(unitState)
    const [deviceInfo, setDeviceInfo] = useState({})
    const [patientInfo, setPatientInfo] = useState() // 선택한 환자 정보
    const [vitals, setVitals] = useState({})
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [isPageReady, setIsPageReady] = useState(false);
    const [vitalArr, setVitalArr] = useState(['HR', 'PR', 'SpO2', 'RESP', 'awRR', 'NIBP', 'IBP1', 'IBP2', 'IBP3', 'IBP4', 'EtCO2', 'InCO2', 'Temp1', 'Temp2', 'IPI', 'GasCo2', 'GasO2', 'GasN2O', 'GasDes', 'GasSev'])
    const valueStringKeys = { ...valueStringForPlayer }
    
    // Unit 재 세팅
    const getUnits = async () => {
        try{
            const unitRes = await axios.get(`/measure/parameter/categories`)
            if (unitRes.code != '200') throw unitRes            
            setUnitInfo(unitRes.data.categories)
        }
        catch(err){
            console.warn(err)
        }
    }

    //Unit 세팅
    const setUnitInfo = (categories) => {
        if (!categories) return
        let nibpUnit = categories.find(category => category.name === 'NIBP')
        nibpUnit = nibpUnit.unit || nibpUnit.defaultUnit

        let ibpUnit = categories.find(category => category.name === 'IBP')
        ibpUnit = ibpUnit.unit || ibpUnit.defaultUnit

        let co2Unit = categories.find(category => category.name === 'CO2')
        co2Unit = co2Unit.unit || co2Unit.defaultUnit

        let tempUnit = categories.find(category => category.name === 'TEMP')
        tempUnit = tempUnit.unit || tempUnit.defaultUnit

        let gasUnit = categories.find(category => category.name === 'GAS')
        gasUnit = gasUnit.unit || gasUnit.defaultUnit

        setUnits({
            ...units,
            'NIBP': nibpUnit,
            'IBP': ibpUnit,
            'IBP1': ibpUnit,
            'IBP2': ibpUnit,
            'IBP3': ibpUnit,
            'IBP4': ibpUnit,
            'EtCO2': co2Unit,
            'InCO2': co2Unit,
            'Temp': tempUnit,
            'Temp1': tempUnit,
            'Temp2': tempUnit,
            'GAS': gasUnit
        })
    }

    // ✅ Device & Patient 정보 가져오기 & 다국어를 위한 유저 정보도 가져오기
    const getReportInfo = async () => {
         try {
            // User 정보 가져오기 (다국어 세팅 위해)
            const resUser = await axios.get('/user/me')
            if (resUser.code != '200') throw resUser

            //언어 세팅
            i18n.changeLanguage(resUser.data.language);

            const deviceId = queryParams.deviceId // 디폴트 값 설정 

            const resDevice = await axios.get(`/device/${deviceId}`);
            if (resDevice.code != '200') throw resDevice
            setDeviceInfo(resDevice.data);
            if (resDevice.data.deviceModel === 'V20') {
                valueStringKeys['RESP'] = 'respRate'
                valueStringKeys['InCO2'] = 'fiCo2'
                setVitalArr(['HR/PR', 'SpO2', 'Temp', 'RESP', 'NIBP', 'IBP', 'EtCO2', 'InCO2', 'IPI' ])
            }

            //과거 시간의 데이터를 가져와야 할 수 있어서 device 기준이 아닌 그때 시간의 기준으로 데이터를 가져온다.
            const resHistory = await axios.get(`/device/${queryParams.deviceId}/patient/histories?startDate=${moment.utc(queryParams.startTime).format('YYYY-MM-DDTHH:mm:ss')}Z&endDate=${moment.utc(queryParams.startTime).format('YYYY-MM-DDTHH:mm:ss')}Z`);
            if (resHistory.code != '200') throw resHistory
                    
            const latestHistory = resHistory?.data?.latestHistory;
            if(latestHistory?.patientId == null || latestHistory?.patientId == undefined){
                setPatientInfo({
                    name : 'Not Admitted',
                });
            }else{
                if(latestHistory.action == 'UNASSIGNED'){
                    setPatientInfo({
                        name : 'Not Admitted',
                    });                    
                }else{
                    const resPatient = await axios.get(`/patient/${latestHistory?.patientId}`);
                    if (resPatient.code != '200') throw resPatient
                    
                    setPatientInfo(resPatient.data);     
                }
            }         
            getUnits();
        } catch (patientError) {
            console.warn('환자 정보 가져오기 실패:', patientError);
        }
    }

    const getVitals = async () => {
        try {
            let newVitals = {}
            if (deviceInfo.deviceModel === 'V20') {
                const res = await axios.get(`/trend/hl7/disconnect/vitals?startTime=${moment.utc(queryParams.startTime).format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment.utc(queryParams.startTime).format('YYYY-MM-DDTHH:mm:ss')}Z&device=V20`)
                if (res.code != '200') throw res
                newVitals = { ...res.data.datas[0] }
            } else {
                const res = await axios.get(`/trend/view/vitals?serialNo=${queryParams.serialNo}&eventTime=${moment.utc(queryParams.startTime).format('YYYY-MM-DDTHH:mm:ss')}Z`);
                if (res.code != '200') throw res
                newVitals = { ...res.data.data[0] }
            }
            const bpSeries = ['NIBP', 'IBP', 'IBP1', 'IBP2', 'IBP3', 'IBP4']
            bpSeries.forEach((item) => {
                if (units[item] === 'kPa') {
                    newVitals[`${item.toLowerCase()}Sys`] = mmHgToKPa(newVitals[`${item.toLowerCase()}Sys`])
                    newVitals[`${item.toLowerCase()}Dia`] = mmHgToKPa(newVitals[`${item.toLowerCase()}Dia`])
                    newVitals[`${item.toLowerCase()}Map`] = mmHgToKPa(newVitals[`${item.toLowerCase()}Map`])
                }
            })

            const co2Series = ['EtCO2', 'InCO2']
            co2Series.forEach((item) => {
                if (units[item] === 'kPa') {
                    newVitals[valueStringKeys[item]] = mmHgToKPa(newVitals[valueStringKeys[item]])
                }else if (units[item] === '%') {
                    newVitals[valueStringKeys[item]] = mmHgToPercent(newVitals[valueStringKeys[item]])
                }
            })

            const tempSeries = ['Temp', 'Temp1', 'Temp2']
            tempSeries.forEach(item => {
                if (units[item] === '°F') {
                    newVitals[valueStringKeys[item]] = celsiusToFahrenheit(newVitals[valueStringKeys[item]])
                } else {
                    if (isNaN(parseFloat(newVitals[valueStringKeys[item]])?.toFixed(1))) {
                        newVitals[valueStringKeys[item]] = null
                    } else {
                        newVitals[valueStringKeys[item]] = parseFloat(newVitals[valueStringKeys[item]])?.toFixed(1)
                    }
                }
            })

            if (units['GAS'] === 'mmHg') {
                newVitals[valueStringKeys['GasO2Ex']] = percentTommHg(newVitals[valueStringKeys['GasO2Ex']])
                newVitals[valueStringKeys['GasO2In']] = percentTommHg(newVitals[valueStringKeys['GasO2In']])
                newVitals[valueStringKeys['GasCo2Ex']] = percentTommHg(newVitals[valueStringKeys['GasCo2Ex']])
                newVitals[valueStringKeys['GasCo2In']] = percentTommHg(newVitals[valueStringKeys['GasCo2In']])
                newVitals[valueStringKeys['GasSevEx']] = percentTommHg(newVitals[valueStringKeys['GasSevEx']])
                newVitals[valueStringKeys['GasSevIn']] = percentTommHg(newVitals[valueStringKeys['GasSevIn']])
                newVitals[valueStringKeys['GasDesEx']] = percentTommHg(newVitals[valueStringKeys['GasDesEx']])
                newVitals[valueStringKeys['GasDesIn']] = percentTommHg(newVitals[valueStringKeys['GasDesIn']])
                newVitals[valueStringKeys['GasN2OEx']] = percentTommHg(newVitals[valueStringKeys['GasN2OEx']])
                newVitals[valueStringKeys['GasN2OIn']] = percentTommHg(newVitals[valueStringKeys['GasN2OIn']])
            } else if (units['GAS'] === 'kPa') {
                newVitals[valueStringKeys['GasO2Ex']] = percentToKpa(newVitals[valueStringKeys['GasO2Ex']])
                newVitals[valueStringKeys['GasO2In']] = percentToKpa(newVitals[valueStringKeys['GasO2In']])
                newVitals[valueStringKeys['GasCo2Ex']] = percentToKpa(newVitals[valueStringKeys['GasCo2Ex']])
                newVitals[valueStringKeys['GasCo2In']] = percentToKpa(newVitals[valueStringKeys['GasCo2In']])
                newVitals[valueStringKeys['GasSevEx']] = percentToKpa(newVitals[valueStringKeys['GasSevEx']])
                newVitals[valueStringKeys['GasSevIn']] = percentToKpa(newVitals[valueStringKeys['GasSevIn']])
                newVitals[valueStringKeys['GasDesEx']] = percentToKpa(newVitals[valueStringKeys['GasDesEx']])
                newVitals[valueStringKeys['GasDesIn']] = percentToKpa(newVitals[valueStringKeys['GasDesIn']])
                newVitals[valueStringKeys['GasN2OEx']] = percentToKpa(newVitals[valueStringKeys['GasN2OEx']])
                newVitals[valueStringKeys['GasN2OIn']] = percentToKpa(newVitals[valueStringKeys['GasN2OIn']])
            }

            setVitals(newVitals)
            return true;
        } catch (err) { 
            console.warn(err) 
            return false;
        }
    }

    // 프린터 함수
    const handlePrint = () => {
        console.log('프린트 시작');
        
        // 클래스 제거 후 다시 추가 (리프레시 효과)
        document.documentElement.classList.remove("waveform-active");
        document.body.classList.remove("waveform-active");
        
        setTimeout(() => {
            document.documentElement.classList.add("waveform-active");
            document.body.classList.add("waveform-active");
            
            setTimeout(() => {
                console.log('페이지 수:', document.querySelectorAll('.a4-page').length);
                window.print();
            }, 500);
        }, 100);
    };

    useEffect(() => {    
        setQueryParams({
            deviceId: searchParams.get('deviceid'),
            patientId : searchParams.get('patientid'),
            startTime: searchParams.get('starttime'),
            endTime: searchParams.get('endtime'),
            serialNo : searchParams.get('serialno'),
            eventMessage : searchParams.get('eventmessage'),
            waveInfo : JSON.parse(searchParams.get('wavesinfo'))
        });        

        //Page 스타일링
        const style = document.createElement("style");
            style.innerHTML = `
            @page {
                size: 3508px 2480px;
                margin: 0;
            }
            `;
        document.head.appendChild(style);

        // 스타일 클래스 추가
        document.documentElement.classList.add("waveform-active");
        document.body.classList.add("waveform-active");        

        return () => {
            document.documentElement.classList.remove("waveform-active");
            document.body.classList.remove("waveform-active");
            document.head.removeChild(style); // 라우트 빠져나갈 때 제거
        };
    }, []);

    useEffect(() => {
        if(isFirstRender.current){
            isFirstRender.current = false;
            return;
        }
        
        getReportInfo();
               
    }, [queryParams])

    useEffect(() => {
        if(queryParams.serialNo && queryParams.startTime){
            const loadAllData = async () => {
                //console.log('데이터 로딩 시작...');
                
                try {
                    // 모든 비동기 작업을 병렬로 실행
                    const [vitals] = await Promise.all([
                        getVitals() //환자 history 가져오기
                    ]);

                    if (vitals) {
                        console.log('모든 데이터 로딩 완료');
                        setIsDataLoaded(true);
                    } else {
                        console.error('데이터 로딩 실패');
                    }
                } catch (error) {
                    console.error('데이터 로드 중 에러:', error);
                }
            };
            loadAllData();

        }
    }, [units])

    useEffect(() => {
        if (isDataLoaded) {
            // DOM 렌더링 및 Canvas 그리기 완료를 위한 충분한 대기 시간
            const renderTimer = setTimeout(() => {
                //console.log('페이지 렌더링 완료');
                setIsPageReady(true);
            }, 1500); // Canvas 렌더링을 고려한 여유 시간

            return () => clearTimeout(renderTimer);
        }
    }, [isDataLoaded]);

    //페이지 준비 완료 시 자동 프린트
    useEffect(() => {
        if (isPageReady) {
            // URL 파라미터로 자동 프린트 제어
            const urlParams = new URLSearchParams(window.location.search);
            const autoPrint = urlParams.get('autoPrint');
            const delay = parseInt(urlParams.get('delay')) || 3000;
            
            if (autoPrint === 'true' || autoPrint === null) { // 기본값은 자동 프린트
                setTimeout(() => {
                    handlePrint(); // 자동 프린트 잠시 스탑
                }, delay);
            }
        }
    }, [isPageReady]);

    //인쇄 하거나 취소하고 창 꺼지기
    window.onafterprint = () => {
        setTimeout( () => {
            window.close();
        },1000)
    };

    // 스타일 정의
    const patientInfoFontBold = 'text-black font-medium text-[48px] leading-[110%] font-pretendard';
    const patientInfoFont = 'text-black font-light text-[36px] leading-[40px] font-pretendard';
    const vitalFont = 'text-black font-light text-[32px] leading-[110%] font-pretendard';

    return (
        <div className="document-container bg-gray-100 p-20 font-sans min-h-screen">
            <div className="a4-page-landscape mx-auto bg-white shadow-lg relative box-border">
                <div className="w-full h-full flex flex-col px-[146px] pt-[140px] pb-[66px]">                    
                    <div>
                        <div className="w-full flex flex-col pb-[24px] border-b-[4px] border-[#5E5E5E] ">
                            <div className='w-full flex items-center justify-start gap-[122px]'>
                                <div className="flex gap-[12px]">
                                    <strong className={patientInfoFontBold}>{t('print.common.patientId')} : </strong>
                                    <strong className={patientInfoFontBold}>{patientInfo?.devicePatientId || '---'}</strong>
                                </div>
                                <div className="flex gap-[12px]">
                                    <strong className={patientInfoFontBold}>{t('print.common.name')} : </strong>
                                    <strong className={patientInfoFontBold}>{patientInfo?.name || '---'}</strong>
                                </div>
                            </div>
                        </div>
                        <div className='w-full flex items-center justify-between py-[32px] border-b-[4px] border-[#5E5E5E]/50 mb-[80px]'>
                            <div className="flex gap-[12px]">
                                <span className={patientInfoFont}>{t('print.common.admitted')} : </span>
                                <span className={patientInfoFont}>{patientInfo?.createdAt ? moment(patientInfo?.createdAt).format('YYYY-MM-DD HH:mm') : '---'}</span>
                            </div>
                            <div className="flex gap-[12px]">
                                <span className={patientInfoFont}>{t('print.common.category')} : </span>
                                <span className={patientInfoFont}>{patientInfo?.category || '---'}</span>
                            </div>
                            <div className="flex gap-[12px]">
                                <span className={patientInfoFont}>{t('print.common.dateOfBirth')} : </span>
                                <span className={patientInfoFont}>{patientInfo?.birthDate || '---'}</span>
                            </div>
                            <div className="flex gap-[12px]">
                                <span className={patientInfoFont}>{t('print.common.gender')} : </span>
                                <span className={patientInfoFont}>{patientInfo?.gender === 'M' ? t('print.common.male') : patientInfo?.gender === 'F' ? t('print.common.female') : t('print.common.notSelected')}</span>
                            </div>
                            <div className="flex gap-[12px]">
                                <span className={patientInfoFont}>{t('print.common.height')}/{t('print.common.weight')} : </span>
                                <span className={patientInfoFont}>{patientInfo?.height || '0'}cm / {patientInfo?.weight || '0'}kg</span>
                            </div>
                        </div>                        
                    </div>
                    <div className="mb-[80px]">
                        <div className="relative mb-[24px]">
                            <strong className={`text-black font-medium text-[40px] leading-[110%] font-pretendard`}>{`${queryParams?.eventMessage} (${moment(queryParams?.startTime).format('YYYY-MM-DD HH:mm:ss')} ~ ${moment(queryParams?.endTime).format('YYYY-MM-DD HH:mm:ss')})`}</strong>
                        </div>
                        <div className='w-full' style={{ minHeight: deviceInfo.deviceModel !== 'V20' ? 275 : 140, maxHeight: deviceInfo.deviceModel !== 'V20' ? 275 : 140 }}>
                            <div className="h-full gap-[6px] flex flex-col text-[#DEDEDE]">                                
                                <div className="bg-[#EFEFEF] flex-1 flex flex-wrap overflow-hidden" style={{ borderLeft: '1px solid #AEAEAE',borderTop: '1px solid #AEAEAE',borderRight: '1px solid #AEAEAE', borderRadius: 16 }}>
                                    {
                                        vitalArr.map((param, idx) => {
                                            return (
                                                <div key={`vitals-${idx}`} className="flex font-light" style={{ minHeight: 68, maxHeight: 68, borderBottom: '1px solid #AEAEAE', width: '20%' }}>
                                                    {
                                                        ['GasCo2', 'GasO2', 'GasN2O', 'GasDes', 'GasSev'].includes(param) ?
                                                        <div className={`${vitalFont} h-full px-[24px] flex items-center`} style={{ minWidth: 280, maxWidth: 280 }}>{`${param} (Ex/In)`}</div> :
                                                        <div className={`${vitalFont} h-full px-[24px] flex items-center`} style={{ minWidth: 280, maxWidth: 280 }}>{param}</div>
                                                    }
                                                    {
                                                        ['HR', 'SpO2', 'RESP', 'awRR', 'EtCO2', 'InCO2', 'Temp', 'Temp1', 'Temp2', 'PR', 'HR/PR', 'IPI'].includes(param) ? 
                                                        <div className={`${vitalFont} h-full px-[24px] flex items-center bg-[#fff] flex-1`} style={{ minWidth: 280 }}>{`${checkEmptyValue(vitals[valueStringKeys[param]])} ${units[param]}`}</div> :
                                                        ['NIBP', 'IBP', 'IBP1', 'IBP2', 'IBP3', 'IBP4'].includes(param) ?
                                                        <div className={`${vitalFont} h-full px-[24px] flex items-center bg-[#fff] flex-1`} style={{ minWidth: 280 }}>
                                                            {`${checkEmptyValue(vitals[valueStringKeys[`${param}Sys`]])}/${checkEmptyValue(vitals[valueStringKeys[`${param}Dia`]])}/${checkEmptyValue(vitals[valueStringKeys[`${param}Map`]])} ${units[param]}`}
                                                        </div> :
                                                        <div className={`${vitalFont} h-full px-[24px] flex items-center bg-[#fff] flex-1`} style={{ minWidth: 280 }}>
                                                            {`${checkEmptyValue(vitals[valueStringKeys[`${param}Ex`]])}/${checkEmptyValue(vitals[valueStringKeys[`${param}In`]])} ${units['GAS']}`}
                                                        </div>
                                                    }
                                                </div>
                                            )
                                        }
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {
                        deviceInfo.deviceModel !== 'V20' &&
                        <div className="mb-[80px]">
                            {
                                deviceInfo && patientInfo && vitals && 
                                <Wave 
                                    alarmWaves={queryParams?.waveInfo || []}
                                    startTime={queryParams?.startTime}
                                    serialNo={queryParams?.serialNo}
                                />
                            }
                        </div>
                    }
                </div>
            </div>        
        </div>
    )
}

export default AlarmForm